import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useEndpoints } from "../../api/ApiContext";
import type { InternalReceivableRow, ReceivableKindPath, ReceivableOutstanding, WriteOffRequest } from "../../api/staffTypes";
import { CursorListView } from "../../components/CursorListView";
import { Banner, Button, Card, EmptyState, Input, Money, PageHeader, QueryView, RadioGroup, Select, StatusPill, Table, Textarea } from "../../components/ui";
import { staffKeys } from "../../layout/StaffShell";
import { amountValidationMessage, compareDecimal, subtractDecimal, validateAmountInput } from "../../lib/money";
import { useCursorList } from "../../lib/useCursorList";
import { isUuid, MoneyConfirmDialog, Mono, reasonError, shortId, StaffRejection, StepUpNote, useStaffCommand, when } from "./staffCommon";

// Receivable write-off (ARCHITECTURE.md §4, migration 0037;
// receivable_write_off.go). One surface for both directions: a lost
// chargeback's advertiser receivable, or a standing publisher debt case.
// Scoped to the originating case, never the party's whole balance. The
// write consumes the case's remaining allowance first, then expense.
// Not dual-controlled by design (receivable_write_off.go:36-45).
// Migration 0068 (G-50/G-53/G-54/G-55): the write now needs a recent MFA
// step-up (it moves real money), is idempotent (it already replayed
// under 0037), typed refusals, and GET /v1/internal/receivables lists
// every outstanding receivable (optionally only write-off-eligible ones)
// so this screen no longer has to be opened by a case id someone
// already has.

const KIND_LABEL: Record<ReceivableKindPath, string> = {
  "advertiser-receivable": "Advertiser receivable (lost chargeback)",
  "publisher-debt": "Publisher debt (debt case)",
};
const PATH_KIND: Record<string, ReceivableKindPath> = {
  advertiser_receivable: "advertiser-receivable",
  publisher_debt: "publisher-debt",
};

export function WriteOffsPage() {
  const ep = useEndpoints();
  const navigate = useNavigate();
  const [kind, setKind] = useState<ReceivableKindPath | "">("");
  const [caseId, setCaseId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [filterKind, setFilterKind] = useState<"advertiser_receivable" | "publisher_debt" | "">("");
  const [writableOnly, setWritableOnly] = useState(true);
  const list = useCursorList(
    ["staff-receivables", filterKind, writableOnly],
    (cursor) => ep.staff.receivables.list(filterKind || undefined, writableOnly, cursor),
    (p) => p.receivables,
  );

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!kind) return setError("Choose the kind of receivable.");
    if (!isUuid(caseId)) return setError("Enter the originating case id (a UUID): the chargeback or the debt case.");
    setError(null);
    navigate(`/staff/write-offs/${kind}/${caseId.trim().toLowerCase()}`);
  }
  return (
    <div className="stack-lg">
      <PageHeader title="Receivable write-offs" description="Write off what's still outstanding on one case, against the allowance provisioned for it." />
      <Card
        title="Outstanding receivables"
        subtitle="Every 0067 origin (a lost chargeback, a standing debt case, a double-payment correction) with outstanding > 0 (G-50)."
        actions={
          <div className="row">
            <Select
              label="Kind"
              value={filterKind}
              onChange={(e) => setFilterKind(e.target.value as "advertiser_receivable" | "publisher_debt" | "")}
              options={[
                { value: "", label: "Every kind" },
                { value: "advertiser_receivable", label: "Advertiser receivable" },
                { value: "publisher_debt", label: "Publisher debt" },
              ]}
            />
            <RadioGroup
              legend="Filter"
              name="writable-filter"
              value={writableOnly ? "writable" : "all"}
              onChange={(v) => setWritableOnly(v === "writable")}
              options={[
                { value: "writable", label: "Write-off eligible" },
                { value: "all", label: "All outstanding" },
              ]}
            />
          </div>
        }
      >
        <CursorListView list={list} loadingLabel="Loading receivables" empty={<EmptyState title="Nothing outstanding">Nothing outstanding matches this filter.</EmptyState>}>
          {(rows) => (
            <Table
              caption="Outstanding receivables"
              captionHidden
              rows={rows}
              rowKey={(r) => r.case_id}
              columns={[
                {
                  key: "case",
                  header: "Case",
                  cell: (r: InternalReceivableRow) => <Link to={`/staff/write-offs/${PATH_KIND[r.receivable_kind]}/${r.case_id}`}>{shortId(r.case_id)}</Link>,
                },
                { key: "kind", header: "Kind", cell: (r) => KIND_LABEL[PATH_KIND[r.receivable_kind]!] },
                { key: "source", header: "Origin", cell: (r) => r.case_source.replace(/_/g, " ") },
                { key: "out", header: "Outstanding", numeric: true, cell: (r) => <Money value={r.outstanding} currency={r.currency} /> },
                { key: "state", header: "State", cell: (r) => r.case_state.replace(/_/g, " ") },
                { key: "writable", header: "Write-off eligible", cell: (r) => (r.state_permits_write_off ? "Yes" : "No") },
                { key: "opened", header: "Opened", cell: (r) => when(r.opened_at) },
              ]}
            />
          )}
        </CursorListView>
      </Card>
      <Card title="Open by case id" subtitle="Or jump straight to a case you already have the id for.">
        <form className="stack" onSubmit={submit} noValidate>
          <Select
            label="Kind"
            value={kind}
            placeholder="Choose…"
            onChange={(e) => setKind(e.target.value as ReceivableKindPath)}
            options={(Object.keys(KIND_LABEL) as ReceivableKindPath[]).map((k) => ({ value: k, label: KIND_LABEL[k] }))}
          />
          <Input label="Originating case id" mono value={caseId} onChange={(e) => setCaseId(e.target.value)} />
          {error ? <Banner tone="error" live="alert">{error}</Banner> : null}
          <div>
            <Button type="submit" variant="secondary">
              Open
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

export function WriteOffDetailPage() {
  const { kind = "", caseId = "" } = useParams();
  const ep = useEndpoints();
  const k = kind as ReceivableKindPath;
  const q = useQuery({ queryKey: staffKeys.receivable(k, caseId), queryFn: () => ep.staff.receivables.get(k, caseId) });
  return (
    <div className="stack-lg">
      <PageHeader title={`Write-off · ${KIND_LABEL[k] ?? kind} ${shortId(caseId)}`} back={{ to: "/staff/write-offs", label: "Write-offs" }} />
      <QueryView query={q} loadingLabel="Loading the receivable">
        {(r) => <Receivable r={r} kind={k} />}
      </QueryView>
    </div>
  );
}

function Receivable({ r, kind }: { r: ReceivableOutstanding; kind: ReceivableKindPath }) {
  const nothingLeft = compareDecimal(r.outstanding, "0") <= 0;
  return (
    <div className="stack">
      <Card
        title="Outstanding"
        actions={r.state_permits_write_off ? <StatusPill tone="info">Can be written off</StatusPill> : <StatusPill tone="neutral">State doesn't permit a write-off</StatusPill>}
      >
        <dl className="definition-list">
          <dt>Case state</dt>
          <dd>
            {r.case_state.replace(/_/g, " ")}
            {kind === "publisher-debt" ? (
              <>
                {" "}
                · <Link to={`/staff/debt-cases/${r.case_id}`}>Open the debt case</Link>
              </>
            ) : null}
          </dd>
          <dt>Party</dt>
          <dd className="mono">{r.party_id}</dd>
          <dt>Recognized</dt>
          <dd>
            <Money value={r.recognized} currency={r.currency} />
          </dd>
          <dt>Written off so far</dt>
          <dd>
            <Money value={r.written_off_total} currency={r.currency} />
          </dd>
          <dt>Outstanding</dt>
          <dd>
            <strong>
              <Money value={r.outstanding} currency={r.currency} />
            </strong>
          </dd>
          <dt>Allowance provisioned / remaining</dt>
          <dd>
            <Money value={r.allowance_provisioned} currency={r.currency} /> / <Money value={r.allowance_remaining} currency={r.currency} />
          </dd>
        </dl>
      </Card>
      <Card title="Write-off history">
        <Table
          caption="Past write-offs on this case"
          captionHidden
          rows={r.write_offs}
          rowKey={(w) => w.write_off_id}
          empty={<p className="muted small">Nothing written off yet.</p>}
          columns={[
            { key: "at", header: "When", cell: (w) => when(w.written_off_at) },
            { key: "amt", header: "Amount", numeric: true, cell: (w) => <Money value={w.amount} currency={r.currency} /> },
            { key: "allow", header: "From allowance", numeric: true, cell: (w) => <Money value={w.allowance_consumed} currency={r.currency} /> },
            { key: "exp", header: "To expense", numeric: true, cell: (w) => <Money value={w.expense_recognized} currency={r.currency} /> },
            { key: "after", header: "Outstanding after", numeric: true, cell: (w) => <Money value={w.outstanding_after} currency={r.currency} /> },
            { key: "who", header: "Reviewer", cell: (w) => <Mono>{shortId(w.actor_user_id)}</Mono> },
            { key: "why", header: "Reason", cell: (w) => w.reason },
          ]}
        />
      </Card>
      <Card title="Collections" subtitle="Migration 0067's collection history: Stripe payments, deposit netting, payout offsets and their reinstatement.">
        <dl className="definition-list">
          <dt>Collected so far</dt>
          <dd>
            <Money value={r.collection.collected} currency={r.currency} />
          </dd>
          <dt>Allowance released / consumed by write-offs</dt>
          <dd>
            <Money value={r.collection.allowance_released} currency={r.currency} /> / <Money value={r.collection.allowance_consumed_by_write_offs} currency={r.currency} />
          </dd>
        </dl>
        <Table
          caption="Collections"
          captionHidden
          rows={r.collection.collections}
          rowKey={(c) => c.collection_id}
          empty={<p className="muted small">Nothing collected yet.</p>}
          columns={[
            { key: "at", header: "When", cell: (c) => when(c.collected_at) },
            { key: "amt", header: "Amount", numeric: true, cell: (c) => <Money value={c.amount} currency={r.currency} /> },
            { key: "how", header: "Method", cell: (c) => c.method.replace(/_/g, " ") },
            { key: "allow", header: "Allowance released", numeric: true, cell: (c) => <Money value={c.allowance_amount} currency={r.currency} /> },
            { key: "after", header: "Outstanding after", numeric: true, cell: (c) => <Money value={c.outstanding_after} currency={r.currency} /> },
            { key: "re", header: "Reinstates", cell: (c) => (c.reinstates_collection_id ? shortId(c.reinstates_collection_id) : "—") },
          ]}
        />
      </Card>
      {r.state_permits_write_off && !nothingLeft ? <WriteOffForm r={r} kind={kind} /> : null}
      {nothingLeft ? <Banner tone="info" title="Nothing outstanding">This case has nothing left to write off.</Banner> : null}
    </div>
  );
}

function WriteOffForm({ r, kind }: { r: ReceivableOutstanding; kind: ReceivableKindPath }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<WriteOffRequest | null>(null);
  const cmd = useStaffCommand(
    (req: WriteOffRequest, opts) => ep.staff.receivables.writeOff(kind, r.case_id, req, opts),
    () => {
      setConfirming(null);
      setAmount("");
      setReason("");
      void qc.invalidateQueries({ queryKey: staffKeys.receivable(kind, r.case_id) });
    },
  );

  function submit(e: FormEvent) {
    e.preventDefault();
    const req: WriteOffRequest = { reason: reason.trim() };
    if (amount.trim()) {
      const v = validateAmountInput(amount, r.currency);
      if (!v.ok) return setFormError(amountValidationMessage(v.reason, r.currency));
      if (compareDecimal(v.value, r.outstanding) > 0) return setFormError("That's more than is outstanding on this case.");
      req.amount = v.value;
    }
    const re = reasonError(reason);
    if (re) return setFormError(re);
    setFormError(null);
    cmd.reset();
    setConfirming(req);
  }

  // What the write-off will do (0037's write_off_receivable): the case's
  // remaining allowance is consumed first, the rest goes to expense.
  const amt = confirming?.amount ?? r.outstanding;
  const fromAllowance = compareDecimal(amt, r.allowance_remaining) <= 0 ? amt : r.allowance_remaining;
  const toExpense = subtractDecimal(amt, fromAllowance);
  const after = subtractDecimal(r.outstanding, amt);
  const done = cmd.data;

  return (
    <Card title="Write off">
      {done ? (
        <Banner tone="success" live="polite" title={done.replayed ? "Already written off (same request)" : "Written off"}>
          <Money value={done.amount} currency={r.currency} /> written off; <Money value={done.outstanding_after} currency={r.currency} /> outstanding
          now.
        </Banner>
      ) : null}
      <form className="stack" onSubmit={submit} noValidate>
        <Input
          label={`Amount (${r.currency.toUpperCase()})`}
          optional
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          hint="Leave empty to write off everything outstanding."
        />
        <Textarea label="Reason" hint="Required; recorded with the write-off and its journal entry." value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
        <StepUpNote required />
        <p className="xs muted">One reviewer: the server doesn't dual-control write-offs (0037), but it does now require a fresh verification.</p>
        {formError ? <Banner tone="error" live="alert">{formError}</Banner> : null}
        {cmd.error && !confirming ? <StaffRejection error={cmd.error} /> : null}
        <div>
          <Button type="submit" variant="danger">
            Write off…
          </Button>
        </div>
      </form>
      <MoneyConfirmDialog
        open={!!confirming}
        title="Write this off?"
        amount={amt}
        currency={r.currency}
        effect={
          <>
            Removes <Money value={amt} currency={r.currency} /> from this case's receivable:{" "}
            <Money value={fromAllowance} currency={r.currency} /> from the allowance already provisioned,{" "}
            <Money value={toExpense} currency={r.currency} /> to expense. <Money value={after} currency={r.currency} /> stays outstanding.
            It's a new journal entry; nothing earlier is edited.
          </>
        }
        confirmLabel="Write off"
        busy={cmd.isPending}
        error={confirming ? cmd.error : undefined}
        onCancel={() => setConfirming(null)}
        onConfirm={() => confirming && cmd.run(confirming)}
      />
    </Card>
  );
}
